import { FinancialPropName, ProjectFinancial } from '../../../../models/project/financial/financial.model';
import { Feedstock, FeedstockPropName } from '../../../../models/project/feedstock/feedstock.model';
import { Project } from '../../../../models/project/project.model';
import { ProjectValidationUnit, ValidationItem } from '../../../../models/data-structures/project-validation';
import { FinancialFeedstockValidation } from './financial-feedstock.validation';
import { FinancialIncomeValidation } from './financial-income.validation';
import { FinancialCapexValidation } from './financial-capex.validation';
import { FinancialOpexValidation } from './financial-opex.validation';
import { FinancialInvestmentValidation } from './financial-investment.validation';
import { ValidationInterface } from '../../validation.interface';
import { Product } from '../../../../models/project/plant-specifications/product.model';
import { Fuel } from '../../../../models/project/plant-specifications/fuel.model';
import { PlantSpecificationsPropName } from '../../../../models/project/plant-specifications/plant-specifications.model';
import { FinancialProductValidation } from './financial-product.validation';
import { FinancialFuelValidation } from './financial-fuel.validation';
import { AdditionalProduct } from '../../../../models/project/plant-specifications/additional-product.model';
import Utils from '../../../../util';
import { FinancialAdditionalProductValidation } from './financial-additional_product.validation';

export class FinancialValidation implements ValidationInterface {
  /** The current financial obj that is being validated */
  private readonly financial: ProjectFinancial;
  /** An array of feedstocks that contains the current feedstocks */
  private readonly feedstocks: Feedstock[];
  /** An array of products that contains the current products */
  private readonly products: Product[];

  /** An array of products that contains the current products */
  private readonly additional_products: AdditionalProduct[];
  /** An array of fuels that contains the current fuels */
  private readonly fuels: Fuel[];

  /**
   * Sets the financial obj and feedstocks array, which will be used for validation
   * @param project will be used to set the current financial obj and feedstock array
   * @param isProjectInput is a boolean that determines if a project input form
   * is being validated to make some inputs optional
   */
  constructor(project: Project, private isProjectInput?: boolean) {
    let projectIsValid = true;
    if (!project || !project.feedstocks || !project.financial || !project.plantSpecifications) {
      projectIsValid = false;
    }
    if (projectIsValid) {
      const fin = project.financial;
      if (!fin.capex || !fin.opex || !fin.income) {
        projectIsValid = false;
      }
      const spec = project.plantSpecifications;
      if (!spec || !spec.generatedProducts || !spec.consumedFuels) {
        projectIsValid = false;
      }
    }
    if (projectIsValid) {
      this.financial = new ProjectFinancial(project.financial);
      this.feedstocks = project.feedstocks;
      this.products = project.plantSpecifications.generatedProducts;
      this.additional_products = project.plantSpecifications.additionalProducts;
      this.fuels = project.plantSpecifications.consumedFuels;
    } else {
      throw Error('invalid financial section provided to validation');
    }

  }

  /**
   * Get a validation unit containing the validation data for the current financial obj
   */
  getValidation() {
    const validationUnit = new ProjectValidationUnit();
    this.addFeedstockValidation(validationUnit);
    this.addProductValidation(validationUnit);
    if (Utils.hasFeature("additional_products")){
      this.addAdditionalProductValidation(validationUnit);
    }
    this.addIncomeValidation(validationUnit);
    this.addFuelValidation(validationUnit);
    this.addCapexValidation(validationUnit);
    this.addOpexValidation(validationUnit);
    if (!this.isProjectInput) {
      this.addInvestmentValidation(validationUnit);
    }
    return validationUnit;
  }

  /**
   * returns a boolean that determines if the current financial obj is valid
   */
  isValid(): boolean {
    return this.getValidation().isValid;
  }

  /**
   * Adds validation for feedstock financial
   * @param validationUnit is the validation unit for which the feedstock financial validation will be added to
   */
  private addFeedstockValidation(validationUnit: ProjectValidationUnit) {
    if (!this.feedstocks) throw Error('unable to validate feedstock financial');
    // if no feedstocks add invalid fields to inflate completion percentage
    if (!this.feedstocks || this.feedstocks.length === 0) {
      if (!(validationUnit.tree instanceof ValidationItem)) {
        validationUnit.tree[FinancialPropName.feedstock] = new ValidationItem(
          false,
          FinancialPropName.feedstock,
          [FinancialError.MISSING_FEEDSTOCK]
        );
        validationUnit.isValid = false;
        validationUnit.invalidFields += 6;
      }
    } else {
      for (let i = 0; i < this.feedstocks.length; i++) {
        if (!this.feedstocks[i].name) {
          console.error('can\'t perform validation check on feedstock without a name');
          continue;
        }
        if (this.feedstocks[i].financial) {
          validationUnit.addArrayToModuleResults(
            new FinancialFeedstockValidation(this.feedstocks[i].financial).getValidation(),
            FinancialPropName.feedstock,
            i,
            this.feedstocks[i].name
          );
        } else {
          const matValidationUnit = new ProjectValidationUnit({
            isValid: false,
            validFields: 0,
            invalidFields: 6,
            tree: {}
          });
          validationUnit.addArrayToModuleResults(
            matValidationUnit,
            FeedstockPropName.financial,
            i,
            this.feedstocks[i].name
          );
        }
      }
    }
  }

  /**
   * Adds validation for products financial
   */
  private addProductValidation(validationUnit: ProjectValidationUnit): void {
    if (!this.products) throw Error('unable to validate products');
    for (let i = 0; i < this.products.length; i++) {
      validationUnit.addArrayToModuleResults(
        new FinancialProductValidation(this.products[i]).getValidation(),
        PlantSpecificationsPropName.generatedProducts,
        i,
        this.products[i].name
      );
    }
  }

  /**
   * Adds validation for additional products financial
   */
  private addAdditionalProductValidation(validationUnit: ProjectValidationUnit): void {
    if (!this.additional_products) throw Error('unable to validate products');
    for (let i = 0; i < this.additional_products.length; i++) {
      validationUnit.addArrayToModuleResults(
        new FinancialAdditionalProductValidation(this.additional_products[i]).getValidation(),
        PlantSpecificationsPropName.additionalProducts,
        i,
        this.additional_products[i].name
      );
    }
  }

  /**
   * Adds validation for income
   * @param validationUnit is the validation unit for which the income validation will be added to
   */
  private addIncomeValidation(validationUnit: ProjectValidationUnit) {
    if (this.financial && this.financial.income && this.financial.income.length > 0) {
      for (let i = 0; i < this.financial.income.length; i++) {
        validationUnit.addArrayToModuleResults(
          new FinancialIncomeValidation(this.financial.income[i]).getValidation(),
          FinancialPropName.income,
          i,
          this.financial.income[i].name
        );
      }
    }
  }

  /**
   * Adds validation for capex
   * @param validationUnit is the validation unit for which the capex will be added to
   */
  private addCapexValidation(validationUnit: ProjectValidationUnit) {
    if (this.financial && this.financial.capex && this.financial.capex.length > 0) {
      for (let i = 0; i < this.financial.capex.length; i++) {
        validationUnit.addArrayToModuleResults(
          new FinancialCapexValidation(this.financial.capex[i]).getValidation(),
          FinancialPropName.capex,
          i,
          this.financial.capex[i].name
        );
      }
    } else {
      validationUnit.setInvalid(FinancialPropName.capex, [FinancialError.MISSING_CAPEX]);
      validationUnit.invalidFields += 7;
    }
  }

  /**
   * Adds validation for opex
   * @param validationUnit is the validation unit for which the opex will be added to
   */
  private addOpexValidation(validationUnit: ProjectValidationUnit) {
    if (this.financial && this.financial.opex && this.financial.opex.length > 0) {
      for (let i = 0; i < this.financial.opex.length; i++) {
        validationUnit.addArrayToModuleResults(
          new FinancialOpexValidation(this.financial.opex[i], this.isProjectInput).getValidation(),
          FinancialPropName.opex,
          i,
          this.financial.opex[i].name
        );
      }
    } else {
      validationUnit.setInvalid(FinancialPropName.opex, [FinancialError.MISSING_OPEX]);
      validationUnit.invalidFields += 10;
    }
  }

  /**
   * Adds validation for fuels financial
   */
  private addFuelValidation(validationUnit: ProjectValidationUnit): void {
    if (!this.fuels) throw Error('unable to validate fuels');
    for (let i = 0; i < this.fuels.length; i++) {
      validationUnit.addArrayToModuleResults(
        new FinancialFuelValidation(this.fuels[i]).getValidation(),
        FinancialPropName.consumedFuels,
        i,
        this.fuels[i].name
      );
    }
  }

  /**
   * Adds validation for investment finances
   * @param validationUnit is the validation unit for which the investment finances will be added to
   */
  addInvestmentValidation(validationUnit: ProjectValidationUnit) {
    if (!this.financial || !this.financial.investment) {
      throw Error('unable to validate investment');
    }
    const invFin = this.financial.investment;
    validationUnit.addModuleResults(
      new FinancialInvestmentValidation(invFin).getValidation(),
      FinancialPropName.investment
    );
  }
}

/**
 * An enum that contains errors for financial validation
 */
enum FinancialError {
  MISSING_FEEDSTOCK = 'VALIDATION.MISSING_FEEDSTOCK',
  MISSING_CAPEX = 'VALIDATION.MISSING_CAPEX',
  MISSING_OPEX = 'VALIDATION.MISSING_OPEX',
}

